import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { MatPaginatorIntl } from '@angular/material/paginator';

import { routes } from './app.routes';
import { environment } from '@environments/environment';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Core imports
import { SharedModule } from './shared/shared.module';
import { CustomIconsService } from '@core/custom-icons/custom-icons.service';
import { DanishMatPaginatorIntl } from '@app/core/services/matPaginator-translation.service';

// Auth
import { AuthModule, LogLevel, DefaultLocalStorageService, AbstractSecurityStorage } from 'angular-auth-oidc-client';
import { AuthInterceptor } from '@core/http-interceptors/auth-interceptor';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { provideIconFontSet } from '@abook-ui/utils/provide-icon-font-set';

// document.baseURI resolves <base href> to an absolute URL. With base href "/" it equals
// window.location.origin; unlike the origin, it still points at the app when served from a sub-path.
const appBaseUrl = document.baseURI.replace(/\/$/, '');

export const appConfig: ApplicationConfig = {
  providers: [
    provideIconFontSet(),
    provideRouter(routes, withViewTransitions()),

    provideHttpClient(withXhr(), withInterceptorsFromDi()),

    // Import providers from existing modules
    importProvidersFrom(
      SharedModule,
      AuthModule.forRoot({
        config: {
          authority: `${environment.authServer}/.well-known/openid-configuration`,
          redirectUrl: appBaseUrl,
          postLogoutRedirectUri: appBaseUrl,
          // GitHub Pages redirects the slash-less redirect_uri
          // (.../pr-N/admin) to .../pr-N/admin/ with a 301. The library
          // otherwise compares the landing URL against redirectUrl as exact
          // strings before treating ?code= as a callback, so on a sub-path
          // base href the code is never exchanged and login loops. The
          // redirect_uri has to stay slash-less: that is the form registered
          // on the auth server (RedirectUris).
          // State, nonce and PKCE are still validated.
          checkRedirectUrlWhenCheckingIfIsCallback: false,
          clientId: 'abook.www.v3.client',
          scope: 'openid profile email abook.api.v3.scope.access offline_access',
          responseType: 'code',
          silentRenew: true,
          useRefreshToken: true,
          unauthorizedRoute: '/authentication/unauthorized',
          logLevel: LogLevel.Warn,
        },
      }),
    ),

    // App Initializers
    provideAppInitializer(() => {
      const customIconsService = inject(CustomIconsService);
      return customIconsService.init();
    }),

    // The session has to be settled before the router makes its first decision: app
    // initializers finish before the router's first navigation starts. This used to run from
    // AppComponent.ngOnInit, where the router was already navigating while checkAuth() was in
    // flight, so every place that asked too early was told "not signed in" — and an ordinary
    // login went through /authentication/unauthorized on its way in. Same pattern as the
    // library's own withAppInitializerAuthCheck().
    //
    // catchError: bootstrap now waits for a network call, and an auth server that does not
    // answer must not leave a blank page behind. The app starts as "not signed in", and the
    // signin route asks again from there.
    provideAppInitializer(() =>
      inject(AuthenticationService)
        .checkUser()
        .pipe(
          catchError((error: unknown) => {
            console.error('Could not settle the login session at startup', error);

            return of(false);
          }),
        ),
    ),

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },

    // Storage
    {
      provide: AbstractSecurityStorage,
      useClass: DefaultLocalStorageService,
    },

    // Material Paginator Danish translations
    {
      provide: MatPaginatorIntl,
      useClass: DanishMatPaginatorIntl,
    },
  ],
};
